import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Search, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { QuickEnquiryModal } from '@/components/QuickEnquiryModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Electronics + Devices', 'Home + Lifestyle', 'Antiques + Collectibles', 'Auto Parts + Accessories',
  'Fashion + Luxury', 'Health + Fitness', 'Tools + Machinery', 'Camping + Outdoors'
];

export default function Buyers() {
  const navigate = useNavigate();
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const loadBuyers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (interestFilter) params.set('interest', interestFilter);
      const { data } = await axios.get(`${API}/buyers?${params}`, { withCredentials: true });
      setBuyers(data || []);
    } catch (e) {
      toast.error('Failed to load buyers');
    } finally {
      setLoading(false);
    }
  }, [search, interestFilter]);

  useEffect(() => {
    const t = setTimeout(loadBuyers, 250);
    return () => clearTimeout(t);
  }, [loadBuyers]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#474747] flex items-center gap-2" data-testid="buyers-title">
            <Users className="w-6 h-6" /> Buyer CRM
          </h1>
          <p className="text-sm text-gray-500">{buyers.length} buyers • every enquiry builds your database</p>
        </div>
        <Button onClick={() => setEnquiryOpen(true)} className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="log-enquiry-button">
          <Plus className="w-4 h-4 mr-2" /> Log Enquiry
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search name, phone or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="buyers-search-input" />
        </div>
        <Select value={interestFilter || 'all'} onValueChange={(v) => setInterestFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[210px]" data-testid="interest-filter-select">
            <SelectValue placeholder="All interests" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All interests</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-[#E9E9E9] overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-gray-50 border-b border-[#E9E9E9]">
            <tr>
              {['Buyer', 'Contact', 'Interests', 'Enquiries', 'Purchases', 'Total Spent', 'Last Activity'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E9E9E9]">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : buyers.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-500">No buyers yet — log your first enquiry to start building the database</td></tr>
            ) : buyers.map(b => (
              <tr key={b.id} onClick={() => navigate(`/buyer/${b.id}`)} className="hover:bg-gray-50 cursor-pointer" data-testid={`buyer-row-${b.id}`}>
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-[#474747] flex items-center gap-1.5">
                    {b.name}
                    {b.vip && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold"><Star className="w-3 h-3 fill-amber-500 text-amber-500" />VIP</span>}
                  </p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{b.phone || b.email || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1 max-w-[240px]">
                    {(b.interests || []).slice(0, 3).map(i => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[#F7FBEC] border border-[#C6E76C] text-[10px] font-medium text-[#3D4237]">{i.split(' + ')[0]}</span>
                    ))}
                    {(b.interests || []).length > 3 && <span className="text-[10px] text-gray-400">+{b.interests.length - 3}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">{b.enquiry_count || 0}</td>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">{b.purchase_count || 0}</td>
                <td className="px-4 py-3 text-sm font-semibold text-[#474747]">${Number(b.total_spent || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(b.last_activity_at || b.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <QuickEnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} onSaved={loadBuyers} />
    </div>
  );
}
